// lib/rosterIdentity.mjs — R120 (Madel/Jesse: "an invited cleaner doesn't show
// up and can't see her assigned cleans"; "the HK portal doesn't show future
// cleans") — which roster row a LOGIN works as.
//
// A login points at one roster row through tech_user_links ({kind, roster_id}).
// Nothing kept that pointer valid: the office archived the invite-made row as
// "the empty duplicate" while its twin held the turns, and the cleaner portal
// trusted the link blindly (HousekeepingPage.resolveCleaner), so she saw
// "Nothing today" for an archived row with no turns. The same copy of the
// rule sat in TasksPage (My Work) and MaintenancePage.
//
// Contract C8 (frozen):
//   resolveRosterId({ link, kind, rows, email, rowsLoaded })
//     → { id: string|null, via: 'link'|'email'|null, stale_link: boolean, ambiguous: boolean }
//   - the link wins only when its target is among the ACTIVE rows of `kind`;
//   - rows that failed to load (rowsLoaded false) keep the link id — never a
//     false "your profile was removed" alarm on a network blip;
//   - a stale link (archived or missing target) falls back to an ACTIVE row
//     with the login's email, flagged stale_link;
//   - a link of ANOTHER kind falls back to the email match, not flagged;
//   - more than one active email match → the first created, flagged ambiguous.
// Emails compare trimmed and case-insensitive; an empty email matches nothing.
//
// Additive helpers (same rule, one place): isActiveRow, techLinkState,
// inspectorNames, duplicateOf, viewAsMe.
// Pure (no I/O); types in lib/rosterIdentity.d.mts; tests: node lib/rosterIdentity.test.mjs.

const normEmail = (s) => String(s == null ? '' : s).trim().toLowerCase();

// A roster row is live unless it was removed: cleaners / inspectors are
// soft-deleted with active:false, vendors with archived:true.
export function isActiveRow(r) {
  return !!r && r.active !== false && !r.archived;
}

const createdKey = (r) => String((r && r.created_at) || '');

export function resolveRosterId({ link = null, kind = '', rows = [], email = '', rowsLoaded } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const loaded = rowsLoaded === undefined ? Array.isArray(rows) : !!rowsLoaded;
  const sameKind = !!(link && link.roster_id && link.kind === kind);
  const linkId = sameKind ? String(link.roster_id) : null;
  if (!loaded) {
    // Rows unknown: trust the link as stored (the pre-R120 behaviour), never alarm.
    return { id: linkId, via: linkId ? 'link' : null, stale_link: false, ambiguous: false };
  }
  const active = list.filter(isActiveRow);
  if (linkId && active.some(r => String(r.id) === linkId)) {
    return { id: linkId, via: 'link', stale_link: false, ambiguous: false };
  }
  const stale = !!linkId;
  const em = normEmail(email);
  const matches = em ? active.map((r, i) => ({ r, i })).filter(x => normEmail(x.r.email) === em) : [];
  if (!matches.length) return { id: null, via: null, stale_link: stale, ambiguous: false };
  // the first created (stored order breaks a tie / a missing created_at)
  matches.sort((a, b) => {
    const ca = createdKey(a.r), cb = createdKey(b.r);
    if (ca && cb && ca !== cb) return ca < cb ? -1 : 1;
    if (!!ca !== !!cb) return ca ? -1 : 1;
    return a.i - b.i;
  });
  return { id: String(matches[0].r.id), via: 'email', stale_link: stale, ambiguous: matches.length > 1 };
}

// The state of a login's stored link against ALL rows of its store (archived
// included): 'ok' | 'archived' | 'missing' | null (no link, or the store could
// not be read — unknown is never reported as broken).
export function techLinkState(link, rows, rowsLoaded = Array.isArray(rows)) {
  if (!link || !link.roster_id) return null;
  if (!rowsLoaded || !Array.isArray(rows)) return null;
  const row = rows.find(r => r && String(r.id) === String(link.roster_id));
  if (!row) return 'missing';
  return isActiveRow(row) ? 'ok' : 'archived';
}

// The names a login's inspections may be assigned under (assignments store a
// free-text NAME): its profile full name, plus every ACTIVE inspector-roster
// (ao_managers) row tied to the login — by its tech link (kind 'inspector') or
// by the same email. "Lacey(IHC)" with Lacey's email → her Mine view finds
// those jobs too. Deduped case-insensitively, profile name first; never an email.
export function inspectorNames({ fullName = '', email = '', link = null, roster = [] } = {}) {
  const out = [];
  const seen = new Set();
  const push = (s) => {
    const n = String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
    if (!n || n.includes('@') || seen.has(n.toLowerCase())) return;
    seen.add(n.toLowerCase());
    out.push(n);
  };
  push(fullName);
  const em = normEmail(email);
  const linkId = link && link.kind === 'inspector' && link.roster_id ? String(link.roster_id) : null;
  for (const r of (Array.isArray(roster) ? roster : [])) {
    if (!isActiveRow(r)) continue;
    if ((linkId && String(r.id) === linkId) || (em && normEmail(r.email) === em)) push(r.name);
  }
  return out;
}

// "Possible duplicate": another ACTIVE row with the same email, or the same
// normalized name (case, spacing and punctuation ignored). → { [id]: otherId }
// (the first such other row in stored order); rows with no twin are absent.
export const normName = (s) => String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
export function duplicateOf(rows) {
  const act = (Array.isArray(rows) ? rows : []).filter(isActiveRow);
  const out = {};
  for (const r of act) {
    const em = normEmail(r.email), nm = normName(r.name);
    const twin = act.find(o => o !== r && String(o.id) !== String(r.id)
      && ((em && normEmail(o.email) === em) || (nm && normName(o.name) === nm)));
    if (twin) out[String(r.id)] = String(twin.id);
  }
  return out;
}

// View-as (main.ts): the viewed person's /api/me — the admin's own answer with
// every identity field replaced by the viewed user's (view-context `ctx`).
// R120: inspector_names and tech_link_state ride along, so the Inspections
// "Mine" view and the cleaner portal resolve exactly as that person's own login.
// R120 review fixes: inspector_roster (the server-resolved inspector row,
// #10) and links_unavailable (#9) are the viewed person's too — the admin's
// own answer never leaks into the viewed identity.
export function viewAsMe(me, ctx) {
  const c = ctx || {};
  const names = Array.isArray(c.inspector_names) ? c.inspector_names.filter(x => typeof x === 'string' && x) : (c.full_name ? [String(c.full_name)] : []);
  const out = {
    ...(me || {}),
    role: c.role,
    profile: { id: c.id, email: c.email, full_name: c.full_name, role: c.role, is_active: true },
    tech_link: c.tech_link || null,
    shared: !!c.shared,
    inspector_names: names,
    tech_link_state: c.tech_link_state === undefined ? null : c.tech_link_state,
    inspector_roster: c.inspector_roster && typeof c.inspector_roster === 'object' ? c.inspector_roster : null,
  };
  if (c.links_unavailable === true) out.links_unavailable = true; else delete out.links_unavailable;
  return out;
}
